/* @jsxImportSource @emotion/react */
/* eslint-disable formatjs/no-literal-string-in-jsx */
import type { ReactNode } from 'react'
import { css, keyframes } from '@emotion/react'
import { FormattedMessage } from 'react-intl'

import ComplianceLogos from '../../../assets/compliance-logos.svg'
import { Button } from '../../../components/Button/Button'
import { WebsiteText } from '../../../components/WebsiteText'
import { useRoutes } from '../../../routes'
import { ScribbleSvg } from './Scribble'

/* Each word/element fades + rises into place. prefers-reduced-motion users
   (and any environment where the animation never runs) see it fully visible. */
const wordReveal = keyframes`
  from {
    opacity: 0;
    transform: translateY(0.25em);
    filter: blur(5px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
    filter: blur(0);
  }
`

const STEP_MS = 170

const AnimatedWord = ({
  children,
  delay,
}: {
  children: ReactNode
  delay: number
}) => (
  <span
    css={css`
      display: inline-block;
      opacity: 0;
      animation: ${wordReveal} 0.5s cubic-bezier(0.2, 0.65, 0.3, 1) ${delay}ms
        both;

      @media (prefers-reduced-motion: reduce) {
        opacity: 1;
        animation: none;
      }
    `}
  >
    {children}
  </span>
)

/* Wraps the formatted title parts so each word reveals in sequence, left to
   right, at a readable pace. The whole sentence stays in the DOM as text. */
const AnimatedTitle = ({ parts }: { parts: ReactNode[] }) => {
  let step = 0
  const items: ReactNode[] = []

  parts.flat().forEach((part, partIndex) => {
    if (typeof part === 'string') {
      // Split into words while keeping the whitespace between them intact.
      part.split(/(\s+)/).forEach((token, tokenIndex) => {
        if (token === '') return
        if (/^\s+$/.test(token)) {
          items.push(token)
          return
        }
        items.push(
          <AnimatedWord
            key={`w-${partIndex}-${tokenIndex}`}
            delay={step++ * STEP_MS}
          >
            {token}
          </AnimatedWord>
        )
      })
      return
    }

    items.push(
      <AnimatedWord key={`e-${partIndex}`} delay={step++ * STEP_MS}>
        {part}
      </AnimatedWord>
    )
  })

  return <>{items}</>
}

export const MobileHeroText = () => {
  const routes = useRoutes()
  return (
    <div
      style={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'end',
      }}
    >
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
        }}
      >
        <WebsiteText
          variant="heroTitle"
          className="position-relative margin-left-auto margin-right-auto text-align-center margin-bottom-40"
          style={{
            zIndex: 0,
            letterSpacing: '-0.03em',
            lineHeight: 1.5,
          }}
        >
          <FormattedMessage
            id="new.hero.title.mobile"
            defaultMessage="A source of truth for your team. And the AI they work with."
          >
            {(...parts) => <AnimatedTitle parts={parts as ReactNode[]} />}
          </FormattedMessage>
        </WebsiteText>
        <WebsiteText
          variant="body-lg"
          className="position-relative margin-left-auto margin-right-auto text-align-center margin-bottom-24"
          style={{
            paddingRight: 40,
            paddingLeft: 40,
            zIndex: 0,
            fontSize: 16,
            color: 'rgba(0, 0, 0, 0.6)',
          }}
        >
          <FormattedMessage
            id="new.hero.subtitle.mobile"
            defaultMessage="Slite builds and maintains the knowledge your business depends on, so your team and their AI work from reliable answers."
          />
        </WebsiteText>
        <div
          style={{
            position: 'absolute',
            zIndex: -1,
            width: '100vw',
            height: '100%',
            top: '3vh',
            left: -20,
            overflow: 'hidden',
          }}
        >
          <ScribbleSvg />
        </div>
      </div>
      <div className="width-full margin-bottom-32 flexbox flexbox-centered flexbox-gap-24 flexbox-wrap">
        <img
          // eslint-disable-next-line formatjs/no-literal-string-in-jsx
          alt="AICPA SOC 2 logo"
          src={ComplianceLogos}
          style={{ maxWidth: 180 }}
        />
        <Button
          size="huge"
          variant="primary-brand"
          href={routes.bookDemo()}
          style={{
            height: 64,
            background: 'var(--saturn-orange)',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '0 32px',
            fontWeight: 600,
            fontSize: 17,
          }}
        >
          <FormattedMessage id="new.hero.cta" defaultMessage="Talk to us" />
        </Button>
      </div>
    </div>
  )
}
